/**
 * Cross-store subscriptions using Zustand's subscribe API.
 *
 * Call `initStoreWiring()` once at app startup (inside App.tsx).
 * Returns a cleanup function that tears down all subscriptions.
 *
 * Wired reactions:
 *   Auth → all stores:  On logout, clear cart, shift, sync, tenant + localStorage cursor.
 *   Location → catalog: On store switch, pull the new location's catalog immediately.
 *   Network → sync:     When the health check sees the server return, load the
 *                       device key if the till started offline, then push (T19).
 *   Cart → draft lock:  Refresh the claim on the server draft in the cart every
 *                       minute; release it when the cart lets go (QA GP2-003).
 */

import { posDb } from '@/db/pos-database';
import { useAuthStore } from '@/stores/use-auth-store';
import { useShiftStore } from '@/stores/use-shift-store';
import { useSyncStore } from '@/stores/use-sync-store';
import { useTenantStore } from '@/stores/use-tenant-store';
import { usePrinterStore } from '@/stores/use-printer-store';
import { useNotificationStore } from '@/stores/use-notification-store';
import { useLocationStore } from '@/stores/use-location-store';
import { useNetworkStore } from '@/stores/use-network-store';
import { useCartStore } from '@/stores/use-cart-store';
import { pullAllUpdates } from '@/services/catalog-sync.service';
import { billingService } from '@/services/billing.service';
import { retryDeadEntries } from '@/services/sync-queue.service';
import { beginDeviceSession, isDeviceSessionActive } from '@/services/device-session.service';

/**
 * T19: the server is reachable again. A till that was started (or reloaded)
 * offline never got its device key, so the bills it sealed earlier cannot be
 * read — fetch the key first, then push everything, DEAD rows included.
 */
export async function resumeSyncOnReconnect(): Promise<void> {
  try {
    if (!isDeviceSessionActive()) await beginDeviceSession();
    await retryDeadEntries();
  } catch {
    /* the next reconnect, the slow DEAD probe or 'Sync now' tries again */
  }
  // gst-parked 120: parks made offline join the store's server list.
  await useCartStore.getState().syncParkedBills().catch(() => undefined);
}

/** How often the till refreshes its claim on the server draft in the cart. */
export const DRAFT_LOCK_HEARTBEAT_MS = 60_000;

export function initStoreWiring(): () => void {
  const unsubscribers: Array<() => void> = [];

  // ── Auth → all stores: on logout (isAuthenticated goes false), reset everything ──
  let prevAuth = useAuthStore.getState().isAuthenticated;

  unsubscribers.push(
    useAuthStore.subscribe((state) => {
      const nowAuth = state.isAuthenticated;

      if (prevAuth && !nowAuth) {
        // User just logged out — clear all dependent stores atomically.
        // NOTE: active draft (draft_bills) is intentionally NOT cleared here.
        // On explicit logout, logout() calls resetAll() + endDeviceSession() → wipeLocalDb(),
        // which already purges all IDB data. On session expiry (401 redirect path), the
        // active draft MUST survive so the cashier can resume their in-progress bill after
        // re-authenticating. Clearing the cart here caused crash-recovery data loss (Bug #092).
        useShiftStore.getState().clearShift();
        useSyncStore.setState({
          lastPushResult: null,
          lastPullRecords: [],
          conflicts: [],
          conflictsMeta: { page: 1, limit: 20, total: 0, totalPages: 0 },
          selectedConflict: null,
          isLoadingConflict: false,
          isResolvingConflict: false,
          error: null,
          syncStatus: 'idle',
          lastSyncedAt: null,
        });
        // Clear stale sync cursor from localStorage
        localStorage.removeItem('bbpos_last_synced_at');
        // Clear IndexedDB caches — defense-in-depth against cross-session data leaks.
        // Fire-and-forget: user is already logged out, failures are non-critical.
        // pending_sync and draft_bills are intentionally NOT cleared (offline bills / parked carts).
        posDb.products.clear().catch(() => {});
        posDb.customers.clear().catch(() => {});
        posDb.sync_metadata.clear().catch(() => {});
        useTenantStore.setState({
          tenant: null,
          isLoading: false,
          error: null,
        });
        // Disconnect printer and reset store (keep config in localStorage for next session)
        usePrinterStore.getState().disconnect();
        // Clear notifications
        useNotificationStore.getState().reset();
      }

      prevAuth = nowAuth;
    }),
  );

  // ── Location → catalog: a store switch pulls that location's records now ──
  // The product cursor is per location, so a first switch is a full fetch and
  // the grid does not wait up to 5 minutes for the periodic sync.
  let prevLocation = useLocationStore.getState().currentLocationId;

  unsubscribers.push(
    useLocationStore.subscribe((state) => {
      const nowLocation = state.currentLocationId;
      if (nowLocation && nowLocation !== prevLocation && prevLocation !== null) {
        pullAllUpdates().catch(() => {
          /* surfaced via useAppHealthStore inside pullAllUpdates */
        });
      }
      prevLocation = nowLocation;
    }),
  );

  // Hydrate printer config from localStorage on startup
  usePrinterStore.getState().hydrate();

  // ── Network → sync: the health check saw the server come back (T19) ──
  // The browser 'online' event (initAutoSync) never fires when it was the
  // server that was down, nor does it load a missing device key. A second
  // trigger while a push runs only asks processQueue to go again.
  let prevConnected = useNetworkStore.getState().isConnected;

  unsubscribers.push(
    useNetworkStore.subscribe((state) => {
      const nowConnected = state.isConnected;
      if (nowConnected && !prevConnected && useAuthStore.getState().isAuthenticated) {
        void resumeSyncOnReconnect();
      }
      prevConnected = nowConnected;
    }),
  );

  // ── Cart → draft lock (QA GP2-003, owner GPD1) ──
  // While a server draft is in the cart this till keeps refreshing its claim;
  // the claim never lapses on the server, so the till frees it the moment the
  // cart lets go of the bill (park, complete, clear, another bill). Logout
  // releases before the token goes (use-auth-store). Best-effort: the API
  // also releases on park / complete / void.
  let heldBillId = useCartStore.getState().currentBillId;

  unsubscribers.push(
    useCartStore.subscribe((state) => {
      const nowBillId = state.currentBillId;
      if (nowBillId === heldBillId) return;
      const letGo = heldBillId;
      heldBillId = nowBillId;
      if (letGo && useAuthStore.getState().isAuthenticated) {
        billingService.releaseBillLock(letGo).catch(() => undefined);
      }
    }),
  );

  const heartbeat = setInterval(() => {
    const billId = useCartStore.getState().currentBillId;
    if (!billId || !useAuthStore.getState().isAuthenticated || !useNetworkStore.getState().isConnected) return;
    // A 409 means a manager took the bill over — BillingPage's bill:lock toast says so.
    billingService.acquireBillLock(billId).catch(() => undefined);
  }, DRAFT_LOCK_HEARTBEAT_MS);
  unsubscribers.push(() => clearInterval(heartbeat));

  return () => {
    for (const unsub of unsubscribers) unsub();
  };
}
